import { useRouter } from 'next/router'
import { Trans, useTranslation } from 'next-i18next'
import { without } from 'ramda'

import {
  Heading1,
  LinkGrid,
  LinkText,
  Paragraph,
  Section,
  Section1,
  Section2
} from '@/features/Listings/seo/elements'
import { utilityTypes } from '@/features/Listings/seo/search-results-content/content/utilities-filter-content'
import { Label } from '@/components/text'

import { getPopularCitiesByState, toTitleCase } from './utils'

const dealsTypes = ['move-in-specials', 'price-drop']

const propertyTypes = [
  'apartments',
  'townhouses',
  'houses',
  'rooms',
  'studios',
  '1-bedroom',
  '2-bedroom',
  '3-bedroom',
  '4-bedroom'
]

export const isDealsFilterPage = (slug: string) => {
  if (!slug) return false
  return dealsTypes.some(deal => slug.includes(deal))
}

const DealsFilterContent = ({
  state,
  citySlug,
  cityName,
  filterTitle,
  filterSlug,
  isCanadaPage,
  cityNameWithState
}: {
  state: string
  citySlug: string
  cityName: string
  filterTitle: string
  filterSlug: string
  isCanadaPage: boolean
  cityNameWithState: string
}) => {
  const { t } = useTranslation()
  const router = useRouter()
  const isChinese = router.locale === 'zh'
  const cityLinks = getPopularCitiesByState(state)

  const content: Record<
    string,
    { primary: { key: string; default: string }; secondary: string }
  > = {
    'move-in-specials': {
      primary: {
        key: 'seo:move-in-specials deals filter paragraph',
        default:
          'Renting a new place is exciting—but saving money when you move in is even better. That’s why you’re searching for {{ filterTitle }} in {{ cityName }}, {{ stateName }}! Whether it’s reduced rent, free utilities for the first month, or waived deposits, move-in specials help make your transition more affordable.'
      },
      secondary: t(
        'seo:move-in-specials deals filter paragraph 2',
        'Your next home with a special move-in offer is just a click away!'
      )
    },
    'price-drop': {
      primary: {
        key: 'seo:price-drop deals filter paragraph',
        default:
          'Finding an affordable rental can be tough - but price drops make it easier. That’s why you’re searching for {{ filterTitle }} in {{ cityName }}, {{ stateName }}! Landlords are lowering rates to attract renters, which means you can snag a great home for less.'
      },
      secondary: t(
        'seo:price-drop deals filter paragraph 2',
        'Your next home at a lower price is just a click away!'
      )
    }
  }

  const dealsFilter =
    dealsTypes.find(slug => filterSlug?.includes(slug)) || null

  const propertyFilter =
    propertyTypes.find(slug => filterSlug?.includes(slug)) || null

  const propertyString = t(
    `filters:b.listing.view.${propertyFilter || 'apartments'}.text`
  )

  if (!dealsFilter) return null

  return (
    <Section>
      <Section1>
        <Section2>
          <Heading1>
            {cityName}
            {isChinese ? '' : ' '}
            {toTitleCase(filterTitle)}
          </Heading1>
          <Paragraph>
            <Trans
              i18nKey={content[dealsFilter].primary.key}
              values={{
                filterTitle: filterTitle?.toLowerCase(),
                cityName,
                stateName: state
              }}
              defaults={content[dealsFilter].primary.default}
            />
          </Paragraph>
          <Paragraph>
            {t(
              'seo:Browse available rentals to see photos, floor plans, and details on amenities, neighborhoods, transit options, and more.'
            )}{' '}
            {content[dealsFilter].secondary}
          </Paragraph>
        </Section2>
      </Section1>
      <Section1>
        <Section2>
          <Heading1>
            {isCanadaPage ? (
              <Trans
                i18nKey="seo:Popular Amenities in Canada"
                values={{
                  filterTitle: toTitleCase(filterTitle)
                }}
                defaults="{{ filterTitle }} in Canada"
              />
            ) : (
              <Trans
                i18nKey="seo:Popular Amenities Nearby"
                values={{
                  filterTitle: toTitleCase(filterTitle)
                }}
                defaults="Nearby {{ filterTitle }}"
              />
            )}
          </Heading1>
        </Section2>
        <LinkGrid linksOnly>
          {without([citySlug], cityLinks).map(slug => {
            const name = t(`cities:city_name_${slug}`)
            return (
              <Label key={slug} fontSize="14px">
                <LinkText href={`/rental-listings/city/${slug}/${filterSlug}`}>
                  {isCanadaPage ? (
                    <Trans
                      i18nKey="seo:amenity for rent in city"
                      values={{
                        filterTitle,
                        cityName: name
                      }}
                      defaults="{{ filterTitle }} in {{ cityName }}"
                    />
                  ) : (
                    <Trans
                      i18nKey="seo:amenity for rent in city state"
                      values={{
                        filterTitle,
                        cityName: name,
                        stateName: state
                      }}
                      defaults="{{ filterTitle }} in {{ cityName }}, {{ stateName }}"
                    />
                  )}
                </LinkText>
              </Label>
            )
          })}
        </LinkGrid>
      </Section1>
      {!isCanadaPage && (
        <Section1>
          <Section2>
            <Heading1>
              <Trans
                i18nKey="seo:deals for rent in city"
                values={{
                  propertyType: propertyString,
                  cityNameWithState
                }}
                defaults="{{ propertyType }} for Rent in {{ cityNameWithState }} with Your Preferred Deals"
              />
            </Heading1>
          </Section2>
          <LinkGrid linksOnly>
            {without([dealsFilter], [...dealsTypes, ...utilityTypes]).map(
              slug => {
                const amenityString = t(`filters:b.listing.view.${slug}.text`)
                return (
                  <Label key={slug} fontSize="14px">
                    <LinkText
                      href={`/rental-listings/city/${citySlug}/${
                        propertyFilter ? `${propertyFilter}-${slug}` : slug
                      }/`}
                    >
                      <Trans
                        i18nKey="seo:property type for rent in city with amenity"
                        values={{
                          propertyType: propertyString,
                          cityNameWithState,
                          amenity: amenityString?.toLowerCase()
                        }}
                        defaults="{{ propertyType }} for rent in {{ cityNameWithState }} with {{ amenity }}"
                      />
                    </LinkText>
                  </Label>
                )
              }
            )}
          </LinkGrid>
        </Section1>
      )}
    </Section>
  )
}

export default DealsFilterContent
